import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  makeStyles,
} from '@material-ui/core';
import {
  TravefyErrorCodes,
  exportTripToTravefy,
  getExportErrorMessage,
  isSafeExternalUrl,
} from '../../helpers/travefy';

export const REEXPORT_WARNING =
  'If you export this trip again, it replaces the trip in Travefy. Any changes you made to it in Travefy will be lost.';

const Status = { IDLE: 'idle', EXPORTING: 'exporting', DONE: 'done', ERROR: 'error' };

const useStyles = makeStyles(() => ({
  root: {
    justifyContent: 'space-between',
  },
}));

const TravefyExport = ({ open, onClose, tripId, link, onConnect, onDisconnect }) => {
  const classes = useStyles();
  const [status, setStatus] = useState(Status.IDLE);
  const [includePrices, setIncludePrices] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(Status.IDLE);
      setResult(null);
      setErrorMessage('');
    }
  }, [open]);

  const handleExport = useCallback(async () => {
    if (status === Status.EXPORTING) return;
    setStatus(Status.EXPORTING);
    try {
      const data = await exportTripToTravefy(tripId, { includePrices });
      setResult(data);
      setStatus(Status.DONE);
    } catch (error) {
      if (error?.code === TravefyErrorCodes.LINK_REQUIRED) {
        // Not linked, or Travefy revoked access: run the connect flow.
        try {
          if (await onConnect()) return;
        } catch {
          // fall through to the error message
        }
      }
      setErrorMessage(getExportErrorMessage(error));
      setStatus(Status.ERROR);
    }
  }, [includePrices, onConnect, status, tripId]);

  const handleDisconnect = useCallback(async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
      onClose();
    } catch {
      setErrorMessage('We could not disconnect Travefy. Try again in a minute.');
      setStatus(Status.ERROR);
    } finally {
      setIsDisconnecting(false);
    }
  }, [onClose, onDisconnect]);

  const handleConnect = useCallback(async () => {
    try {
      await onConnect();
    } catch (error) {
      setErrorMessage(
        error?.code === TravefyErrorCodes.NOT_CONFIGURED
          ? getExportErrorMessage(error)
          : 'We could not start the connection. Try again in a minute.',
      );
      setStatus(Status.ERROR);
    }
  }, [onConnect]);

  const isLinked = !!link?.linked;
  const isBusy = status === Status.EXPORTING || isDisconnecting;
  const canOpenResult = status === Status.DONE && isSafeExternalUrl(result?.externalUrl);

  let body;
  if (!isLinked) {
    body = (
      <p>
        Connect your Travefy account once, then send any trip to Travefy. You will go to Travefy to
        approve the connection and come right back.
      </p>
    );
    if (!isLinked && status === Status.ERROR) {
      body = (
        <>
          {body}
          <p role="alert" className="mt-2 text-red-800">
            {errorMessage}
          </p>
        </>
      );
    }
  } else if (status === Status.DONE) {
    body = <p>{result?.updated ? 'Updated in Travefy.' : 'Sent to Travefy.'}</p>;
  } else {
    body = (
      <>
        {link.fullName && <p className="mb-3">Connected as {link.fullName}</p>}
        <p>Send this trip to your Travefy account.</p>
        <p className="mt-2" data-testid="travefy-reexport-warning">
          {REEXPORT_WARNING}
        </p>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={includePrices}
              onChange={(e) => setIncludePrices(e.target.checked)}
              disabled={isBusy}
            />
          }
          label="Include prices"
        />
        {status === Status.ERROR && (
          <p role="alert" className="mt-2 text-red-800">
            {errorMessage}
          </p>
        )}
      </>
    );
  }

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isLinked ? 'Export to Travefy' : 'Connect to Travefy'}</DialogTitle>
      <DialogContent dividers>{body}</DialogContent>
      <DialogActions classes={classes}>
        {isLinked && status !== Status.DONE ? (
          <Button onClick={handleDisconnect} disabled={isBusy} color="primary">
            Disconnect
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button onClick={onClose} disabled={isBusy} color="primary">
            {status === Status.DONE ? 'Close' : 'Cancel'}
          </Button>
          {!isLinked && (
            <Button onClick={handleConnect} color="primary" variant="contained">
              Connect
            </Button>
          )}
          {isLinked && status !== Status.DONE && (
            <Button onClick={handleExport} disabled={isBusy} color="primary" variant="contained">
              {status === Status.EXPORTING && (
                <CircularProgress color="inherit" className="mr-2" size={15} />
              )}
              {status === Status.ERROR ? 'Try again' : 'Export'}
            </Button>
          )}
          {canOpenResult && (
            <Button
              href={result.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              variant="contained"
            >
              Open in Travefy
            </Button>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};

TravefyExport.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tripId: PropTypes.string.isRequired,
  link: PropTypes.shape({
    linked: PropTypes.bool,
    fullName: PropTypes.string,
  }),
  onConnect: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
};

TravefyExport.defaultProps = { link: null };

export default TravefyExport;
